import { queryClient } from './queryClient';
import { queryKeys } from './queryKeys';

// What a save refreshes in the shared cache. Every API function that changes
// data calls one of these after it succeeds, so no page has to remember to
// reload (enforced by src/shared/api/cacheRefreshRule.test.ts).

// Workspaces you see, their names, your role in them and their members.
export const refreshWorkspaces = () =>
  queryClient.invalidateQueries({ queryKey: queryKeys.organizations.all });

// The signed-in user's profile (name, contact email, EULA, memberships).
export const refreshMyDetails = () =>
  queryClient.invalidateQueries({ queryKey: queryKeys.users.me() });

// Routes and connections: their lists, records, roles and configurations, and
// the destination URL filter list. Logs are left alone — a save doesn't change
// what was already logged. What's on screen reloads now; everything else is
// marked out of date and reloads when a page next shows it, so one save
// doesn't set off a burst of requests for data nobody is looking at.
// The costly derived lists (reverse routes, a provider's route count, the
// Connections role sweep) are only marked out of date: the few saves that
// change them refresh them on the page that made the change.
const DERIVED_ROUTE_LISTS = ['reverse', 'by-provider'];
export const refreshRoutesAndConnections = () =>
  Promise.all([
    queryClient.invalidateQueries({
      queryKey: queryKeys.routes.all,
      predicate: (query) =>
        query.queryKey[2] !== 'logs' &&
        !DERIVED_ROUTE_LISTS.includes(query.queryKey[1] as string),
    }),
    queryClient.invalidateQueries({
      queryKey: queryKeys.routes.all,
      predicate: (query) =>
        DERIVED_ROUTE_LISTS.includes(query.queryKey[1] as string),
      refetchType: 'none',
    }),
    queryClient.invalidateQueries({
      queryKey: queryKeys.connections.all,
      predicate: (query) => query.queryKey[1] !== 'role-map',
    }),
    queryClient.invalidateQueries({
      queryKey: queryKeys.connections.roleMap(),
      refetchType: 'none',
    }),
    // A filter dropdown's options; walking every page of them after each save
    // costs more than it's worth, so they reload on the next page visit.
    queryClient.invalidateQueries({
      queryKey: queryKeys.filterOptions.destinationUrls,
      refetchType: 'none',
    }),
  ]);
